import { supabase } from "@/lib/supabase";

type CacheEntry = {
  name: string;
  expiresAt: number;
};

const DEFAULT_TTL_MS = 5 * 60_000;
const cache = new Map<string, CacheEntry>();
const inflightByKey = new Map<string, Promise<Map<string, string>>>();

function getDisplayName(row: Record<string, unknown>): string {
  const first = String(row.first_name ?? "").trim();
  const last = String(row.last_name ?? "").trim();
  return String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
}

export async function fetchProfileDisplayNames(
  ids: string[],
  ttlMs = DEFAULT_TTL_MS,
): Promise<Map<string, string>> {
  const now = Date.now();
  const normalized = Array.from(
    new Set(ids.map((id) => String(id).trim()).filter(Boolean)),
  ).sort();
  const key = normalized.join("|");
  if (!key) return new Map();

  const inflight = inflightByKey.get(key);
  if (inflight) return inflight;

  const promise = (async () => {
    const result = new Map<string, string>();
    const missing: string[] = [];

    for (const id of normalized) {
      const existing = cache.get(id);
      if (existing && existing.expiresAt > now) {
        result.set(id, existing.name);
      } else {
        missing.push(id);
      }
    }

    if (missing.length > 0) {
      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", missing),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", missing),
      ]);

      const rows = [
        ...((byIdRes.data ?? []) as Record<string, unknown>[]),
        ...((byAuthIdRes.data ?? []) as Record<string, unknown>[]),
      ];

      for (const row of rows) {
        const name = getDisplayName(row);
        if (!name) continue;
        const id = String(row.id ?? "").trim();
        const authUserId = String(row.auth_user_id ?? "").trim();
        const expiresAt = now + ttlMs;
        if (id) {
          cache.set(id, { name, expiresAt });
          result.set(id, name);
        }
        if (authUserId) {
          cache.set(authUserId, { name, expiresAt });
          result.set(authUserId, name);
        }
      }
    }

    return result;
  })();

  inflightByKey.set(key, promise);
  try {
    return await promise;
  } finally {
    inflightByKey.delete(key);
  }
}

